import {
  LayoutDashboard,
  Activity,
  Bell,
  Printer,
  Layers,
  Building2,
  FolderKanban,
  Archive,
  Disc,
  Package,
  ListOrdered,
  Box,
  Briefcase,
  Flame,
  Droplet,
  Square,
  Droplets,
  Wrench,
  PackageOpen,
  Settings,
  UserCog,
  BellRing,
  Shield,
  Users,
  CreditCard,
  Plug,
  Folder,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

export interface Section {
  id: string;
  label: string;
  basePath: string;
  icon: LucideIcon;
  subNav: NavItem[];
}

export const sections: Section[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    basePath: '/',
    icon: LayoutDashboard,
    subNav: [
      { label: 'Overview', path: '/', icon: LayoutDashboard },
      { label: 'Activity', path: '/activity', icon: Activity },
      { label: 'Alerts', path: '/alerts', icon: Bell },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    basePath: '/projects',
    icon: FolderKanban,
    subNav: [
      { label: 'Projects', path: '/projects', icon: FolderKanban },
      { label: 'Jobs', path: '/projects/jobs', icon: Briefcase },
      { label: 'Queue', path: '/projects/queue', icon: ListOrdered },
      { label: 'Models', path: '/models', icon: Box },
      { label: 'Collections', path: '/collections', icon: Folder },
    ],
  },
  {
    id: 'printers',
    label: 'Printers',
    basePath: '/printers',
    icon: Printer,
    subNav: [
      { label: 'Printers', path: '/printers', icon: Printer },
      { label: 'Filament Systems', path: '/printers/filament-systems', icon: PackageOpen },
    ],
  },
  {
    id: 'filaments',
    label: 'Filaments',
    basePath: '/filaments',
    icon: Layers,
    subNav: [
      { label: 'Filaments', path: '/filaments', icon: Layers },
      { label: 'Spools', path: '/spools', icon: Disc },
    ],
  },
  {
    id: 'accessories',
    label: 'Accessories',
    basePath: '/accessories/hotends',
    icon: Package,
    subNav: [
      { label: 'Hotends', path: '/accessories/hotends', icon: Flame },
      { label: 'Nozzles', path: '/accessories/nozzles', icon: Droplet },
      { label: 'Build Plates', path: '/accessories/build-plates', icon: Square },
      { label: 'Consumables', path: '/accessories/consumables', icon: Droplets },
      { label: 'Tools', path: '/accessories/tools', icon: Wrench },
      { label: 'Storage', path: '/storage', icon: Archive },
      { label: 'Other', path: '/accessories/other', icon: Package },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    basePath: '/settings/profile',
    icon: Settings,
    subNav: [
      { label: 'Profile', path: '/settings/profile', icon: UserCog },
      { label: 'Notifications', path: '/settings/notifications', icon: BellRing },
      { label: 'Privacy', path: '/settings/privacy', icon: Shield },
      { label: 'Workspace', path: '/settings/workspace', icon: Building2 },
      { label: 'Team', path: '/settings/team', icon: Users },
      { label: 'Subscription', path: '/settings/subscription', icon: CreditCard },
      { label: 'Integrations', path: '/settings/integrations', icon: Plug },
    ],
  },
];

/**
 * Check if a path belongs to a section (matches any subNav path)
 */
function pathBelongsToSection(section: Section, pathname: string): boolean {
  return section.subNav.some(item =>
    pathname === item.path || pathname.startsWith(item.path + '/')
  );
}

/**
 * Get the current section based on pathname
 */
export function getCurrentSection(pathname: string): Section {
  // Check non-dashboard sections first
  for (const section of sections.slice(1)) {
    if (pathBelongsToSection(section, pathname)) {
      return section;
    }
  }
  // Default to dashboard
  return sections[0];
}

/**
 * Check if a section is active
 */
export function isSectionActive(section: Section, pathname: string): boolean {
  if (section.id === 'dashboard') {
    // Dashboard is active only for its own paths
    return pathBelongsToSection(section, pathname);
  }
  return pathBelongsToSection(section, pathname);
}
